/**
 * Join Household Screen
 *
 * Deep-link target for paprika-app://join/{code}. Looks up the invite, then
 * lets the user bring their existing data into the household or start fresh
 * with the inviter's.
 *
 * The join, the migration and the cleanup all happen in one server call — see
 * api/household/join.ts. This screen only decides *whether* to offer the join,
 * and that decision turns on genuine membership rather than on holding a
 * householdId: a household of one is an artefact, and treating it as a
 * pairing locked people out of invites the rest of the UI was inviting them to
 * accept.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Platform,
  TextInput,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import Svg, { Path } from 'react-native-svg';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
  PaprikaShadows,
} from '../../src/styles/theme';
import { RootState, AppDispatch } from '../../src/redux/store';
import { setHouseholdId } from '../../src/redux/authSlice';
import {
  lookupInvite,
  joinHouseholdViaCode,
  setDisplayName,
  HouseholdError,
  Invite,
} from '../../src/services/householdService';
import { auth } from '../../src/services/firebaseConfig';
import { useHouseholdMembers } from '../../src/hooks/useHouseholdMembers';
import { setPendingInvite, clearPendingInvite } from '../../src/utils/pendingInvite';
import { trackEvent } from '../../src/services/analyticsService';
import { cookFacingError } from '../../src/i18n/cookFacingError';
import { downloadMealPlanFromCloud } from '../../src/redux/mealPlanSlice';
import { downloadGroceryListFromCloud } from '../../src/redux/groceryListSlice';
import { downloadInventoryFromCloud } from '../../src/redux/inventorySlice';
import { downloadRecipeMetadataFromCloud } from '../../src/redux/recipeMetadataSlice';
import { downloadUserPreferencesFromCloud } from '../../src/redux/userPreferencesSlice';

type Choice = 'merge' | 'fresh';

const PaprikaPot = () => (
  <Svg width={80} height={44} viewBox="0 0 120 66" fill="none">
    <Path d="M38 8c2 3-2 5 0 8" stroke={Paprika.accent} strokeWidth={1.4} strokeLinecap="round" opacity={0.7} />
    <Path d="M48 4c2 4-2 6 0 10" stroke={Paprika.accent} strokeWidth={1.4} strokeLinecap="round" opacity={0.7} />
    <Path d="M58 8c2 3-2 5 0 8" stroke={Paprika.accent} strokeWidth={1.4} strokeLinecap="round" opacity={0.7} />
    <Path d="M22 28 h52 a2 2 0 0 1 2 2 v18 a14 14 0 0 1-14 14 H34 a14 14 0 0 1-14-14 V30 a2 2 0 0 1 2-2z" stroke={Paprika.accent} strokeWidth={1.4} strokeLinejoin="round" fill="none" />
    <Path d="M18 30 h-6 a4 4 0 0 0 0 8 h6" stroke={Paprika.accent} strokeWidth={1.4} strokeLinecap="round" fill="none" />
    <Path d="M78 30 h6 a4 4 0 0 1 0 8 h-6" stroke={Paprika.accent} strokeWidth={1.4} strokeLinecap="round" fill="none" />
    <Path d="M90 6 c0 8-6 8-6 16" stroke={Paprika.accent} strokeWidth={1.4} strokeLinecap="round" fill="none" />
    <Path d="M84 22 c4 0 6-2 8-4" stroke={Paprika.accent} strokeWidth={1.4} strokeLinecap="round" fill="none" />
  </Svg>
);

const FeatureRow = ({ icon, label }: { icon: React.ComponentProps<typeof Feather>['name']; label: string }) => (
  <View style={featureRowStyle}>
    <View style={featureIconWrap}>
      <Feather name={icon} size={16} color={Paprika.accent} />
    </View>
    <Text style={featureLabelStyle}>{label}</Text>
  </View>
);

const featureRowStyle: import('react-native').ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: PaprikaSpacing.md,
};
const featureIconWrap: import('react-native').ViewStyle = {
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: '#FDF0E8',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: PaprikaSpacing.md,
};
const featureLabelStyle: import('react-native').TextStyle = {
  ...PaprikaTypography.bodyMedium,
  color: Paprika.textPrimary,
  flex: 1,
};

export default function JoinHouseholdScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const params = useLocalSearchParams<{ code: string }>();
  const code = params.code?.toUpperCase() ?? '';

  const { user, isAuthenticated, householdId } = useSelector(
    (state: RootState) => state.auth
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<Invite | null>(null);
  const [joining, setJoining] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const household = useHouseholdMembers();

  // Firebase only fills displayName for Google sign-in. Ask once, here, because
  // this is the moment the name becomes visible to another person.
  const needsName = !auth?.currentUser?.displayName;

  // Look up the invite on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!code) {
        setError(t('join.errMissingCode'));
        setLoading(false);
        return;
      }

      // Stash before anything else, and before the auth branch below decides
      // what to render: a signed-out visitor is about to be sent to /auth to
      // register, and this is the only thing that brings them back here. It
      // also survives a Google signInWithRedirect, which reloads the page and
      // takes the URL with it.
      await setPendingInvite(code);
      trackEvent('household_invite_opened', { signed_in: isAuthenticated });

      try {
        const result = await lookupInvite(code);
        if (cancelled) return;
        if (!result) {
          setError(t('join.errNotFound'));
          await clearPendingInvite();
        } else if (result.status === 'consumed') {
          setError(t('join.errUsed'));
          await clearPendingInvite();
        } else if (result.expiresAt.toMillis() < Date.now()) {
          setError(t('join.errExpired'));
          await clearPendingInvite();
        } else {
          setInvite(result);
        }
      } catch (err) {
        if (!cancelled) {
          // Not cleared: a signed-out visitor cannot read invites/{code} at all
          // (firestore.rules gates `get` on request.auth), so the common failure
          // here is "not signed in yet" — exactly the case the stash exists for.
          // That one is expected and only noted; anything else is an error.
          if ((err as { code?: string })?.code === 'permission-denied') {
            console.warn('[join] invite not readable before sign-in', code);
          } else {
            console.error('[join] invite lookup failed', err);
          }
          setError(cookFacingError(err, t, 'join.errLoad'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const handleJoin = useCallback(
    async (choice: Choice) => {
      if (!user?.uid || !invite) return;
      setJoining(true);
      setError(null);
      trackEvent('household_join_started', { choice });
      try {
        // Before the join, not after: the server copies the account's
        // displayName into the household roster as part of the transaction, so
        // a name saved afterwards would not appear to the partner until the
        // next roster publish.
        if (needsName && nameDraft.trim()) {
          await setDisplayName(nameDraft, null).catch(() => {});
        }

        // 1. The server joins, migrates and cleans up in one call. The merge
        //    used to run here, after the join returned, which left a closed tab
        //    as a half-migrated user — and it read users/{uid}/…, which is the
        //    wrong source for a joiner who was alone in their own household.
        const joined = await joinHouseholdViaCode(invite.code, user.uid, choice);

        if (joined.migrated) {
          const m = joined.migrated;
          console.log(
            `Carried into the household: ${m.savedRecipes} recipes, ` +
              `${m.recipeMetadata} ratings, ${m.inventory} pantry items, ` +
              `${m.mealPlan} weeks, ${m.cookLog} cook-log entries, ` +
              `${m.groceryCustomItems} custom grocery items` +
              (joined.abandonedHouseholdId
                ? ` (from the household ${joined.abandonedHouseholdId}, now removed)`
                : '')
          );
        }

        trackEvent('household_join_completed', {
          choice,
          migrated: !!joined.migrated,
          abandoned_household: !!joined.abandonedHouseholdId,
        });

        // The hand-off has done its job. Leaving it would replay the join on
        // the next sign-in, which now lands on "this invitation has been used".
        await clearPendingInvite();

        // 2. Flip active scope so subsequent reads/writes hit household path.
        dispatch(setHouseholdId(joined.id));

        // 3. Re-pull everything from the new path to repopulate Redux.
        await Promise.all([
          dispatch(downloadMealPlanFromCloud(user.uid)).unwrap().catch(() => {}),
          dispatch(downloadGroceryListFromCloud(user.uid)).unwrap().catch(() => {}),
          dispatch(downloadInventoryFromCloud(user.uid)).unwrap().catch(() => {}),
          dispatch(downloadRecipeMetadataFromCloud(user.uid)).unwrap().catch(() => {}),
          dispatch(downloadUserPreferencesFromCloud(user.uid)).unwrap().catch(() => {}),
        ]);

        router.replace('/');
      } catch (err) {
        const message = cookFacingError(err, t, 'join.errJoin');
        console.error('[join] join failed', err);
        trackEvent('household_join_failed', {
          choice,
          reason: err instanceof HouseholdError ? err.code : 'unknown',
        });
        setError(message);
      } finally {
        setJoining(false);
      }
    },
    [user?.uid, invite, dispatch, router, needsName, nameDraft, t]
  );

  // Not signed in — show branded welcome card with app value prop.
  if (!isAuthenticated || !user) {
    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={styles.welcomeScroll}>
          <View style={styles.welcomeHero}>
            <PaprikaPot />
            <Text style={styles.welcomeApp}>Du Chef</Text>
            <Text style={styles.welcomeHeadline}>{t('join.headline')}</Text>
            <Text style={styles.welcomeSubline}>{t('join.subline')}</Text>
          </View>

          <View style={styles.featureList}>
            <FeatureRow icon="calendar" label={t('join.featurePlan')} />
            <FeatureRow icon="shopping-cart" label={t('join.featureGrocery')} />
            <FeatureRow icon="book-open" label={t('join.featureRecipes')} />
            <FeatureRow icon="package" label={t('join.featurePantry')} />
          </View>

          {/* `next` is the fast path — it survives as long as nothing reloads.
              The durable copy is the stash written in the mount effect, which
              is what a Google signInWithRedirect comes back to. */}
          <View style={styles.ctaBlock}>
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() =>
                router.push({ pathname: '/auth', params: { next: `/join/${code}` } })
              }
            >
              <Text style={styles.primaryBtnText}>{t('join.createAccount')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.secondaryBtn}
              onPress={() =>
                router.push({ pathname: '/auth', params: { next: `/join/${code}` } })
              }
            >
              <Text style={styles.secondaryBtnText}>{t('join.haveAccount')}</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    );
  }

  // Already in this household
  if (householdId && invite && householdId === invite.householdId) {
    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={styles.body}>
          <Feather name="check-circle" size={48} color={Paprika.accent} />
          <Text style={styles.title}>{t('join.alreadyPairedTitle')}</Text>
          <Text style={styles.bodyText}>{t('join.alreadyPairedBody')}</Text>
          <TouchableOpacity
            style={styles.primaryBtn}
            onPress={() => router.replace('/')}
          >
            <Text style={styles.primaryBtnText}>{t('join.continue')}</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
    );
  }

  // Genuinely paired with someone else. Note the test is on real membership,
  // not on holding a householdId: a household of one is an artefact the old
  // Share screen created on mount, and refusing those users locked them out of
  // invites while the household screen simultaneously told them they were solo.
  // Being alone in your own household falls through to the normal join, which
  // carries your data over and tears the empty household down.
  if (household.status === 'paired' && invite && householdId !== invite.householdId) {
    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={styles.body}>
          <Feather name="alert-triangle" size={48} color={Paprika.destructive} />
          <Text style={styles.title}>{t('join.otherHouseholdTitle')}</Text>
          <Text style={styles.bodyText}>{t('join.otherHouseholdBody')}</Text>
          <TouchableOpacity
            style={styles.primaryBtn}
            onPress={() => router.replace('/')}
          >
            <Text style={styles.primaryBtnText}>{t('join.backToApp')}</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <Feather name="x" size={24} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('join.headerTitle')}</Text>
      </View>

      <ScrollView contentContainerStyle={styles.body}>
        {loading && (
          <View style={styles.loading}>
            <ActivityIndicator size="large" color={Paprika.accent} />
            <Text style={styles.loadingText}>{t('join.lookingUp')}</Text>
          </View>
        )}

        {!!error && !loading && (
          <View style={styles.errorCard}>
            <Feather name="alert-circle" size={32} color={Paprika.destructive} />
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => router.replace('/')}
            >
              <Text style={styles.primaryBtnText}>{t('join.backToApp')}</Text>
            </TouchableOpacity>
          </View>
        )}

        {invite && !loading && !error && (
          <>
            <Text style={styles.title}>{t('join.invitedTitle')}</Text>
            <Text style={styles.subtitle}>{t('join.invitedSubtitle')}</Text>

            {needsName && (
              <View style={styles.nameBlock}>
                <Text style={styles.nameTitle}>{t('household.nameTitle')}</Text>
                <Text style={styles.nameBody}>{t('household.nameBody')}</Text>
                <TextInput
                  style={styles.nameInput}
                  value={nameDraft}
                  onChangeText={setNameDraft}
                  placeholder={t('household.namePlaceholder')}
                  placeholderTextColor={Paprika.textMuted}
                  autoCapitalize="words"
                  returnKeyType="done"
                  editable={!joining}
                />
              </View>
            )}

            <Text style={styles.questionText}>{t('join.dataQuestion')}</Text>

            <TouchableOpacity
              style={[styles.choiceCard, styles.choiceMerge]}
              onPress={() => handleJoin('merge')}
              disabled={joining}
            >
              <Text style={styles.choiceTitleMerge}>{t('join.mergeTitle')}</Text>
              <Text style={styles.choiceBody}>{t('join.mergeBody')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.choiceCard, styles.choiceFresh]}
              onPress={() => handleJoin('fresh')}
              disabled={joining}
            >
              <Text style={styles.choiceTitleFresh}>{t('join.freshTitle')}</Text>
              <Text style={styles.choiceBody}>{t('join.freshBody')}</Text>
            </TouchableOpacity>

            {joining && (
              <View style={styles.loading}>
                <ActivityIndicator size="small" color={Paprika.accent} />
                <Text style={styles.loadingText}>{t('join.joining')}</Text>
              </View>
            )}

            <TouchableOpacity
              style={styles.cancelBtn}
              onPress={() => {
                // Declining is an answer. Without this the next sign-in would
                // drag them back to a screen they just walked away from.
                clearPendingInvite();
                router.replace('/');
              }}
              disabled={joining}
            >
              <Text style={styles.cancelText}>{t('join.cancel')}</Text>
            </TouchableOpacity>
          </>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Paprika.background },
  welcomeScroll: {
    flexGrow: 1,
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: Platform.OS === 'web' ? PaprikaSpacing.xxl : 60,
    paddingBottom: PaprikaSpacing.xxl,
  },
  welcomeHero: {
    alignItems: 'center',
    marginBottom: PaprikaSpacing.xxl,
  },
  welcomeApp: {
    ...PaprikaTypography.headingLarge,
    color: Paprika.accent,
    marginTop: PaprikaSpacing.md,
    letterSpacing: 1,
  },
  welcomeHeadline: {
    ...PaprikaTypography.headingLarge,
    color: Paprika.textPrimary,
    marginTop: PaprikaSpacing.sm,
    textAlign: 'center',
  },
  welcomeSubline: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginTop: PaprikaSpacing.sm,
    textAlign: 'center',
    maxWidth: 300,
  },
  featureList: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.border,
    padding: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.xl,
    ...PaprikaShadows.card,
  },
  ctaBlock: {
    gap: PaprikaSpacing.sm,
  },
  secondaryBtn: {
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.md,
  },
  secondaryBtnText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: Platform.OS === 'web' ? PaprikaSpacing.lg : PaprikaSpacing.xxl,
    paddingBottom: PaprikaSpacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.border,
  },
  backBtn: { padding: PaprikaSpacing.xs, marginRight: PaprikaSpacing.sm },
  headerTitle: { ...PaprikaTypography.headingMedium, color: Paprika.textPrimary },
  body: { padding: PaprikaSpacing.lg, alignItems: 'center' },
  title: {
    ...PaprikaTypography.headingLarge,
    color: Paprika.textPrimary,
    marginTop: PaprikaSpacing.lg,
    textAlign: 'center',
  },
  subtitle: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginTop: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.xl,
    textAlign: 'center',
  },
  bodyText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginTop: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.lg,
    textAlign: 'center',
  },
  questionText: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    alignSelf: 'flex-start',
    marginBottom: PaprikaSpacing.md,
  },
  nameBlock: {
    alignSelf: 'stretch',
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    padding: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.xl,
    ...PaprikaShadows.card,
  },
  nameTitle: {
    ...PaprikaTypography.titleLarge,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.xs,
  },
  nameBody: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  nameInput: {
    backgroundColor: Paprika.surfaceHighest,
    borderRadius: PaprikaRadius.md,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: 12,
    marginTop: PaprikaSpacing.md,
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },
  choiceCard: {
    width: '100%',
    borderRadius: PaprikaRadius.lg,
    borderWidth: 2,
    padding: PaprikaSpacing.lg,
    marginBottom: PaprikaSpacing.md,
    ...PaprikaShadows.card,
  },
  choiceMerge: {
    backgroundColor: '#B2F2BB',
    borderColor: '#22C55E',
  },
  choiceFresh: {
    backgroundColor: '#FFD8A8',
    borderColor: '#F59E0B',
  },
  choiceTitleMerge: {
    ...PaprikaTypography.headingMedium,
    color: '#15803D',
    marginBottom: PaprikaSpacing.xs,
  },
  choiceTitleFresh: {
    ...PaprikaTypography.headingMedium,
    color: '#9A5030',
    marginBottom: PaprikaSpacing.xs,
  },
  choiceBody: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },
  loading: {
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.lg,
  },
  loadingText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginTop: PaprikaSpacing.sm,
  },
  errorCard: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.destructive,
    padding: PaprikaSpacing.lg,
    alignItems: 'center',
    width: '100%',
  },
  errorText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.destructive,
    textAlign: 'center',
    marginTop: PaprikaSpacing.sm,
    marginBottom: PaprikaSpacing.md,
  },
  primaryBtn: {
    backgroundColor: Paprika.accent,
    paddingVertical: 14,
    paddingHorizontal: PaprikaSpacing.xl,
    borderRadius: PaprikaRadius.xl,
    marginTop: PaprikaSpacing.md,
  },
  primaryBtnText: {
    ...PaprikaTypography.labelLarge,
    fontSize: 16,
    color: '#FFFFFF',
  },
  cancelBtn: {
    paddingVertical: PaprikaSpacing.md,
    marginTop: PaprikaSpacing.md,
  },
  cancelText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },
});
